import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Table from "components/DesignSystem/Table/V2/Table";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { Tag } from "components/DesignSystem/Tag/Tag";
import AddJurisdiction from "components/Entity/EntityFormEngine/AddJurisdiction";
import DeleteJurisdictions from "components/Entity/EntityFormEngine/DeleteJurisdictions";
import EditJurisdiction from "components/Entity/EntityFormEngine/EditJurisdiction";
import { useModal } from "hooks/useModal";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useGetEntityAllStateDataQuery } from "store/apis/entity";
import { EntityDataAttribute, EntityStateData } from "types/Models/entity";
import { useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { DeleteIcon } from "components/icons/delete";
import { EditPencil } from "components/icons/EditPencil";

const columnHelper = createColumnHelper<EntityStateData>();

const getAttributeValue = (
  attributes: EntityDataAttribute[],
  labelKeyword: string
): EntityDataAttribute["value"] | undefined => {
  const attr = attributes.find((a) =>
    a.label.toLowerCase().includes(labelKeyword.toLowerCase())
  );
  return attr?.value;
};

const AttributeCell = ({
  value,
}: {
  value: EntityDataAttribute["value"] | undefined;
}) => {
  if (value === null || value === undefined || value === "") {
    return <span className="t-text-body-sm t-text-text-30">—</span>;
  }

  const displayValue = String(value);

  return (
    <Tag tagType="purple" icon={false} rounded>
      {displayValue}
    </Tag>
  );
};

const columns = [
  columnHelper.accessor("state", {
    id: "state",
    header: () => <HeadCellContent title="State" />,
    cell: (info) => (
      <span className="t-text-body-sm t-text-text-100">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("registration_type", {
    id: "registration_type",
    size: 200,
    header: () => <HeadCellContent title="Registration Type" />,
    cell: (info) => (
      <span className="t-text-body-sm t-text-text-60 t-capitalize">
        {info.getValue()?.replace(/_/g, " ").toLowerCase() ?? "—"}
      </span>
    ),
  }),
  columnHelper.accessor("foreign_qualified_data_attributes", {
    id: "foreign_qualified",
    size: 220,
    header: () => <HeadCellContent title="Foreign Qualified" />,
    cell: (info) => (
      <AttributeCell
        value={getAttributeValue(info.getValue(), "foreign qualified")}
      />
    ),
  }),
  columnHelper.accessor("foreign_qualified_data_attributes", {
    id: "payroll",
    size: 180,
    header: () => <HeadCellContent title="Has Payroll" />,
    cell: (info) => (
      <AttributeCell value={getAttributeValue(info.getValue(), "payroll")} />
    ),
  }),
  columnHelper.accessor((row) => row, {
    id: "actions",
    size: 80,
    header: () => null,
    cell: (info) => {
      const { state_id, registration_type } = info.getValue();
      type TableMeta = {
        setStateToDelete: (stateId: string) => void;
        setStateToEdit: (stateId: string) => void;
      };
      const { setStateToDelete, setStateToEdit } = info.table.options
        .meta as TableMeta;

      const isHomeState = registration_type === "HOME_STATE";

      return (
        <div className="t-flex t-justify-end t-gap-1">
          <Button
            size="small"
            customType="ghost_icon"
            type="button"
            onClick={() => setStateToEdit(state_id)}
          >
            <span className="t-text-text-30">
              <EditPencil color="currentColor" />
            </span>
          </Button>
          {!isHomeState && (
            <Button
              size="small"
              customType="ghost_icon"
              type="button"
              onClick={() => setStateToDelete(state_id)}
            >
              <span className="t-text-text-30">
                <DeleteIcon color="currentColor" size="16" />
              </span>
            </Button>
          )}
        </div>
      );
    },
  }),
];

const EmptyState = ({ onAdd }: { onAdd: () => void }) => (
  <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-4 t-py-16">
    <p className="t-m-0 t-text-body-sm t-text-text-30">
      No jurisdictions added yet.
    </p>
    <Button customType="primary" size="small" onClick={onAdd}>
      Add Jurisdiction
    </Button>
  </div>
);

export const SITJurisdictions = () => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const addModal = useModal();
  const [stateToDelete, setStateToDelete] = useState<string | null>(null);
  const [stateToEdit, setStateToEdit] = useState<string | null>(null);

  const {
    data = [],
    isLoading,
    isSuccess,
  } = useGetEntityAllStateDataQuery(
    { entityId, groupId },
    { skip: !groupId || !entityId }
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: { setStateToDelete, setStateToEdit },
  });

  const isEmpty = data.length === 0;

  return (
    <>
      <Async.Root isLoading={isLoading} isSuccess={isSuccess} isEmpty={isEmpty}>
        <Async.Empty>
          <EmptyState onAdd={addModal.open} />
        </Async.Empty>
        <Async.Success>
          <div className="t-flex t-flex-col t-gap-4">
            <div className="t-flex t-justify-end">
              <Button customType="primary" size="small" onClick={addModal.open}>
                Add Jurisdiction
              </Button>
            </div>
            <Table.Container>
              <Table.Content>
                <Table.Head>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Table.HeadRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <Table.HeadCell
                          key={header.id}
                          style={{ width: header.getSize() }}
                          isUpperCase={false}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </Table.HeadCell>
                      ))}
                    </Table.HeadRow>
                  ))}
                </Table.Head>
                <Table.Body>
                  {table.getRowModel().rows.map((row) => (
                    <Table.Row key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell
                          key={cell.id}
                          style={{ width: cell.column.getSize() }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.Container>
          </div>
        </Async.Success>
      </Async.Root>

      {addModal.isOpen && (
        <AddJurisdiction
          isOpen={addModal.isOpen}
          close={addModal.close}
          entityId={entityId}
        />
      )}

      {stateToDelete && (
        <DeleteJurisdictions
          isOpen={Boolean(stateToDelete)}
          close={() => setStateToDelete(null)}
          stateId={stateToDelete}
          entityId={entityId}
        />
      )}

      {stateToEdit && (
        <EditJurisdiction
          isOpen={Boolean(stateToEdit)}
          close={() => setStateToEdit(null)}
          stateId={stateToEdit}
          entityId={entityId}
        />
      )}
    </>
  );
};
